import { BRANDS_WITH_REQUIRED_FREIGHT } from '@cannect/utils/constants'
import React, { useEffect, useState } from 'react'
import { FaRegClock } from 'react-icons/fa'
import { FaPix } from 'react-icons/fa6'
import { useHistory } from 'react-router'
import { useFinalizeCheckout } from '../../../../../hooks/useFinalizeCheckout'
import { usePrepareCart } from '../../../../../hooks/usePrepareCart'
import { getEvents } from '../../../../../services/events'
import { BoletoTransaction, paymentService } from '../../../../../services/paymentService'
import { WarningBanners } from '../resumeStep/WarningBanners'

interface BoletoPaymentProps {
  selectedMethod: string
  cartItems: any[]
  code?: string
  expirationDays?: number
  prefetchedTransaction?: BoletoTransaction | null
  autoGenerate?: boolean
  isFromModal?: boolean
}

export const BoletoPayment: React.FC<BoletoPaymentProps> = ({
  selectedMethod: _selectedMethod,
  cartItems: _cartItems,
  code = '',
  expirationDays = 5,
  prefetchedTransaction = null,
  autoGenerate = true,
  isFromModal = false
}) => {
  const [daysLeft, setDaysLeft] = useState(expirationDays)
  const [boletoTransaction, setBoletoTransaction] = useState<BoletoTransaction | null>(null)
  const [isGeneratingBoleto, setIsGeneratingBoleto] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { lastOrderCreated } = usePrepareCart() as any
  const history = useHistory()
  const { finalize } = useFinalizeCheckout()
  const isAllSupplements = lastOrderCreated?.items.every(
    (item: any) =>
      item?.product?.class_id === 6 && !BRANDS_WITH_REQUIRED_FREIGHT.includes(item?.product?.brand_id || item?.brand_id)
  )

  const isPillOnly: boolean = lastOrderCreated?.is_pill_only ?? false
  const hasPillControlledItems = (lastOrderCreated?.items || []).some(
    (item: any) => item.requires_controlled_prescription
  )
  const skipDocuments = isAllSupplements || (isPillOnly && !hasPillControlledItems)

  const generateBoleto = async (): Promise<void> => {
    if (!lastOrderCreated?.id || !lastOrderCreated?.amount) return

    setIsGeneratingBoleto(true)
    setError(null)

    try {
      const transaction = await paymentService.generateBoletoCharge(lastOrderCreated.id, lastOrderCreated.amount)
      setBoletoTransaction(transaction)

      if (transaction.boleto_expiration_date) {
        const expirationDate = new Date(transaction.boleto_expiration_date)
        const now = new Date()
        const diffInDays = Math.ceil((expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
        setDaysLeft(Math.max(0, diffInDays))
      }

      if (transaction.pdf) {
        window.open(transaction.pdf, '_blank')
      } else if (transaction.boleto_url || transaction.url) {
        window.open(transaction.boleto_url || transaction.url, '_blank')
      }
    } catch (err) {
      setError('Erro ao gerar boleto. Tente novamente.')
      console.error('Erro ao gerar boleto:', err)
    } finally {
      setIsGeneratingBoleto(false)
    }
  }

  useEffect(() => {
    if (prefetchedTransaction) {
      setBoletoTransaction(prefetchedTransaction)
      if (prefetchedTransaction.boleto_expiration_date) {
        const expirationDate = new Date(prefetchedTransaction.boleto_expiration_date)
        const now = new Date()
        const diffInDays = Math.ceil((expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
        setDaysLeft(Math.max(0, diffInDays))
      }
      return
    }
    if (autoGenerate && lastOrderCreated?.id) {
      generateBoleto().catch(() => undefined)
    }
  }, [lastOrderCreated?.id, prefetchedTransaction, autoGenerate])

  useEffect(() => {
    if (daysLeft <= 0) return undefined
    const timer = setInterval(
      () => {
        setDaysLeft((prev) => (prev > 0 ? prev - 1 : 0))
      },
      24 * 60 * 60 * 1000
    )
    return () => clearInterval(timer)
  }, [daysLeft])

  const handleCopy = () => {
    const textToCopy = boletoTransaction?.line || boletoTransaction?.boleto_barcode || code
    navigator.clipboard.writeText(textToCopy)
  }

  const handleDownload = () => {
    const pdfUrl = boletoTransaction?.pdf || boletoTransaction?.boleto_url
    if (pdfUrl) {
      window.open(pdfUrl, '_blank')
    } else {
      // TODO: exibir toast aqui!
    }
  }

  const handleWhatsApp = () => {
    const boletoText = `Boleto Bancário - Linha digitável: ${
      boletoTransaction?.line || boletoTransaction?.boleto_barcode || code
    }`
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(boletoText)}`
    window.open(whatsappUrl, '_blank')
  }

  const handleEmail = () => {
    const subject = 'Boleto Bancário - Cannect'
    const body = `Segue a linha digitável do boleto: ${
      boletoTransaction?.line || boletoTransaction?.boleto_barcode || code
    }`
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
    window.open(mailtoUrl)
  }

  if (isGeneratingBoleto) {
    return (
      <div className="mx-auto w-full rounded-xl bg-white p-8 shadow-sm">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-primary-700">
          <FaPix className="h-4 w-4 text-primary-700" />
          Gerando Boleto...
        </h2>
        <div className="mt-6 flex items-center justify-center">
          <div className="h-32 w-32 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="mx-auto w-full rounded-xl bg-white p-8 shadow-sm">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-red-700">
          <FaPix className="h-4 w-4 text-red-700" />
          Erro ao gerar Boleto
        </h2>
        <p className="mt-2 text-red-600">{error}</p>
        <button
          onClick={generateBoleto}
          className="mt-4 rounded-md bg-primary-600 px-4 py-2 text-white hover:bg-primary-700">
          Tentar novamente
        </button>
      </div>
    )
  }

  if (!boletoTransaction) {
    return (
      <div className="mx-auto w-full rounded-xl bg-white p-8 shadow-sm">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-primary-700">
          <FaPix className="h-4 w-4 text-primary-700" />
          Boleto não disponível
        </h2>
        <p className="mt-2 text-gray-600">Clique no botão para gerar o boleto</p>
        <button
          onClick={generateBoleto}
          className="mt-4 rounded-md bg-primary-600 px-4 py-2 text-white hover:bg-primary-700">
          Gerar Boleto
        </button>
      </div>
    )
  }

  return (
    <div className={`mx-auto w-full rounded-xl bg-white ${isFromModal ? 'p-0' : 'p-8'} shadow-sm`}>
      {/* Cabeçalho */}
      {isFromModal ? (
        <div>
          <h2 className="text-center text-lg font-semibold text-primary-700">Pague com Boleto</h2>
          <p className="mt-1 text-center text-sm text-neutral-800">Finalize seu pagamento de forma rápida e segura.</p>
        </div>
      ) : (
        <>
          <h2 className="flex items-center gap-2 text-lg font-semibold text-primary-700">
            <FaPix className="h-4 w-4 text-primary-700" />
            Pagamento via Boleto{' '}
          </h2>
          <p className="mt-1 text-sm text-neutral-800"> Certo! Copie o código de barras para efetuar o pagamento.</p>
        </>
      )}

      {/* Conteúdo em fundo cinza ou branco (modal) */}
      <div className={`mt-6 rounded-lg ${isFromModal ? 'bg-white' : 'bg-[#FAFAFA]'} p-6`}>
        <div className="flex flex-col items-center justify-center gap-8 md:flex-row md:items-start">
          <div className="flex w-full flex-col items-center md:w-1/2">
            <div className="flex h-28 w-full items-center justify-center rounded-md border border-gray-300 bg-white p-4">
              {(() => {
                const imageSrc = boletoTransaction?.barcode || boletoTransaction?.qr_code_url || ''
                if (imageSrc) {
                  return (
                    <img src={imageSrc} alt="Código de barras do boleto" className="h-full w-full object-contain" />
                  )
                }
                return (
                  <div className="flex flex-col items-center gap-2">
                    <div className="flex gap-1">
                      <div className="h-16 w-1 bg-black" />
                      <div className="h-8 w-1 bg-black" />
                      <div className="h-20 w-1 bg-black" />
                      <div className="h-12 w-1 bg-black" />
                      <div className="h-16 w-1 bg-black" />
                      <div className="h-10 w-1 bg-black" />
                      <div className="h-18 w-1 bg-black" />
                      <div className="h-14 w-1 bg-black" />
                      <div className="h-16 w-1 bg-black" />
                      <div className="h-8 w-1 bg-black" />
                      <div className="h-20 w-1 bg-black" />
                      <div className="h-12 w-1 bg-black" />
                      <div className="h-16 w-1 bg-black" />
                      <div className="h-10 w-1 bg-black" />
                      <div className="h-18 w-1 bg-black" />
                      <div className="h-14 w-1 bg-black" />
                    </div>
                    <span className="font-mono text-xs text-gray-600">{boletoTransaction?.line || code}</span>
                  </div>
                )
              })()}
            </div>

            <div className="mt-3 flex h-8 w-full items-center justify-center gap-1 rounded-md bg-[#F2D4BB] px-4 py-1 text-xs text-neutral-800">
              <FaRegClock className="h-3 w-3 text-neutral-800" />
              Expira em: {daysLeft} {daysLeft === 1 ? 'dia' : 'dias'}
            </div>

            <div className="mt-4 w-full">
              <label htmlFor="boleto-line" className="mb-1 block text-sm text-neutral-600">
                Linha digitável
                <input
                  id="boleto-line"
                  style={{
                    border: '1px solid #B5B3B3'
                  }}
                  type="text"
                  value={boletoTransaction.line || boletoTransaction.boleto_barcode || code}
                  readOnly
                  onClick={handleCopy}
                  className="mt-2 h-10 w-full cursor-pointer rounded-md border border-gray-300 bg-[#EDECE7] px-2 py-2 text-base text-gray-700"
                />
              </label>
            </div>
          </div>

          {/* Coluna direita - Botões */}
          <div className="mt-3 flex w-full flex-col justify-end gap-4 md:w-1/2">
            <p className="text-sm text-neutral-800">Se preferir você pode seguir da seguinte forma:</p>

            <button
              onClick={handleDownload}
              style={{
                border: '2px solid #6B7B3C'
              }}
              className="border-5 rounded-md border-primary-800 px-4 py-2 text-sm font-medium text-primary-800 hover:bg-green-50">
              Baixar
            </button>
            <button
              onClick={handleWhatsApp}
              style={{
                border: '2px solid #6B7B3C'
              }}
              className="rounded-md border border-primary-800 px-4 py-2 text-sm font-medium text-primary-800 hover:bg-green-50">
              Enviar por WhatsApp
            </button>
            {/* <button
              onClick={handleEmail}
              style={{
                border: '2px solid #6B7B3C'
              }}
              className="rounded-md border border-primary-800 px-4 py-2 text-sm font-medium text-primary-800 hover:bg-green-50">
              Enviar por Email
            </button> */}
          </div>
        </div>
      </div>

      {isFromModal ? (
        <div className="mt-6 flex w-full justify-center">
          <button
            type="button"
            onClick={() => {
              if (!lastOrderCreated?.id) return
              finalize({
                redirectTo: skipDocuments
                  ? `/detalhes-do-pedido/${lastOrderCreated.id}`
                  : `/enviar-receita/${lastOrderCreated.id}`,
                shouldClearCart: true,
                alsoClearOrderState: true
              })
              getEvents.eventLoadObjects({
                name: 'checkout_redirect',
                category: 'checkout',
                action: 'boleto_already_paid_redirect',
                value: String(lastOrderCreated.id)
              })
              getEvents.eventSendDataLayer({
                eventName: 'checkout_redirect',
                method: isFromModal ? 'card_boleto' : 'boleto',
                trigger: 'already_paid_click',
                orderId: lastOrderCreated.id,
                target: skipDocuments
                  ? `/detalhes-do-pedido/${lastOrderCreated.id}`
                  : `/enviar-receita/${lastOrderCreated.id}`
              })
            }}
            className="w-full rounded-md border-none bg-primary-800 px-6 py-2 text-sm font-medium text-white hover:bg-primary-700">
            Já paguei!
          </button>
        </div>
      ) : (
        <div className="mt-6">
          <WarningBanners
            containsProductWithLongerDeliveryTime
            containProductsMatirz
            containsProductWithHubDistributor={false}
          />
        </div>
      )}
    </div>
  )
}
